/**
 * reminders — the standalone to-dos the Planner's right column holds.
 *
 * PLANNER-3. `tasks` could not carry these: `tasks.project_id` is NOT
 * NULL, so every reminder would have to belong to a project, and
 * "renew the insurance" does not. See
 * supabase/migrations/20261029140000_reminders.sql.
 *
 * UNTIL THAT MIGRATION IS APPLIED
 *
 * Rennan cannot reach the Supabase dashboard; migrations go through
 * Lovable, which means there is a window where this code is deployed
 * and the table is not there. Every read and write therefore
 * distinguishes "the table is missing" from "the write failed", and
 * the panel says which. A reminders column that throws a red toast
 * every time the page loads would be worse than one that says, once,
 * what it is waiting for.
 */
/* eslint-disable @typescript-eslint/no-explicit-any -- `reminders` is
   absent from src/integrations/supabase/types.ts, which is generated
   from the LIVE database, until the migration above is applied. The
   casts here are that gap and should be removed when the types are
   next regenerated. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '@/hooks/useOrgId';

export interface ReminderRow {
  id: string;
  org_id: string;
  title: string;
  /** A day, not a moment. Null = no date: a someday reminder. */
  due_on: string | null;
  /** Optional time of day, beside the date rather than inside it. */
  due_at_time: string | null;
  /** Null = open. */
  done_at: string | null;
  project_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Does this error mean the table isn't there, rather than that the
 * request was bad?
 *
 * Postgres raises 42P01 for an undefined table; PostgREST answers
 * PGRST205 when its schema cache has no such relation, which is what
 * actually comes back through the JS client. Both, plus the message,
 * because the code has moved between PostgREST versions before.
 */
export function isMissingTable(error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code;
  if (code === '42P01' || code === 'PGRST205' || code === 'PGRST202') return true;
  const message = String((error as { message?: string } | null)?.message ?? '');
  return /schema cache|does not exist|undefined table/i.test(message);
}

export interface RemindersResult {
  rows: ReminderRow[];
  /** True when the table isn't there yet. */
  pending: boolean;
}

export const useReminders = () => {
  const { data: orgId } = useOrgId();
  return useQuery<RemindersResult>({
    queryKey: ['reminders', orgId],
    queryFn: async () => {
      if (!orgId) return { rows: [], pending: false };
      const { data, error } = await (supabase as any)
        .from('reminders')
        .select('*')
        .eq('org_id', orgId)
        .order('due_on', { ascending: true, nullsFirst: false });
      if (error) {
        if (isMissingTable(error)) return { rows: [], pending: true };
        throw error;
      }
      return { rows: (data ?? []) as ReminderRow[], pending: false };
    },
    enabled: !!orgId,
    // The table appearing is a one-off event, not something to poll
    // for; a refresh picks it up.
    retry: (count, error) => !isMissingTable(error) && count < 2,
  });
};

export const useCreateReminder = () => {
  const queryClient = useQueryClient();
  const { data: orgId } = useOrgId();
  return useMutation({
    mutationFn: async (input: { title: string; due_on?: string | null; project_id?: string | null }) => {
      if (!orgId) throw new Error('Your studio isn’t set up yet');
      const { error } = await (supabase as any)
        .from('reminders')
        .insert({ ...input, org_id: orgId });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reminders'] });
    },
  });
};

export const useSetReminderDone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) => {
      const { error } = await (supabase as any)
        .from('reminders')
        .update({ done_at: done ? new Date().toISOString() : null, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reminders'] });
    },
  });
};

export const useDeleteReminder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from('reminders').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reminders'] });
    },
  });
};
